<template>
    <aside class="h-full fixed app-sidebar-container">
        <div class="border-noround fixed app-sidebar mt-2 panel-menu">
            <p-button
                v-if="showMessageAction"
                class="text-white p-button-rounded mb-2 mt-1 ml-2"
                label="New Message"
                icon="pi pi-comment"
                @click="showAddMessageModal"
            />
            <p-panel-menu
                v-model:expanded-keys="expandedKeys"
                :model="navItems"
                :unstyled="true"
            >
                <template #item="{ item, props: itemProps }">
                    <router-link
                        :to="item.to || ''"
                        v-bind="itemProps.action"
                        class="p-menuitem-link flex align-items-center"
                        :class="{ 'active-link': item.to ? (route.path.startsWith(item.to)) : hasActiveChild(item) }"
                    >
                        <i v-if="item.icon" :class="[item.icon, 'mr-2']" />
                        <span>{{ item.label }}</span>
                        <p-badge
                            v-if="item.badge"
                            severity="danger"
                            :value="item.badge"
                            class="ml-2"
                        />
                        <i v-if="item.items && item.items.length > 0" :class="[ 'pi ml-auto', expandedKeys[item.key] ? 'pi-chevron-down' : 'pi-chevron-right' ]" />
                    </router-link>
                </template>
            </p-panel-menu>
        </div>
    </aside>
</template>

<script setup lang="ts">
import { MenuItem } from 'primevue/menuitem';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {Modal} from "@/store/classes";
import {useRootStore} from "@/store";

const route = useRoute();
const rootStore = useRootStore();

const props = withDefaults(defineProps<{
    items: MenuItem[];
    showMessageAction?: boolean;
}>(), {
    showMessageAction: false
});

const showModal = (modal: Modal) => rootStore.dispatch('showModal', modal);

const navItems = computed(() => {
    return props.items.map(x => ({
        ...x,
        key: (x.key || x.label)?.toString()
    }))
})

const expandedKeys = ref<Record<string, boolean>>({});

const hasActiveChild = (item: MenuItem, currentPath: string = route.path): boolean => {
    if (!item.items || item.items.length === 0) return false;
    return item.items.some(child => currentPath.startsWith(child.to));
};

watch(() => route.path, path => {
    const newKeys: Record<string, boolean> = {};
    
    // Find parent with active child and expand them
    navItems.value.forEach(item => {
        if (hasActiveChild(item, path) && item.key) {
            newKeys[item.key] = true;
        }
    });
    
    expandedKeys.value = newKeys;
}, { immediate: true });

async function showAddMessageModal() {
    await showModal({
        name: 'MessageModal',
        props: {
            title: 'Compose Message'
        }
    });
}
</script>

<style scoped lang="scss">
$darkblue: #333f48;
$offwhite: #f0ecec;

.app-sidebar,
.app-sidebar-container {
    top: 48px;
    min-height: calc(100vh - 48px);
    width: 256px;
    overflow: hidden;
    background-color: $offwhite;
}

:deep(.app-sidebar a) {
    color: $darkblue;
    display: inline-block;
    width: 100%;
    height: 100%;
    &:hover {
        background-color: #e3dfdf !important;
    }
}

:deep([data-pc-section="header"] a) {
    padding: 12px 20px;
    &.active-link {
        background-color: $darkblue;
        color: white !important;
        &:hover {
            background-color: #2a353d !important;
        }
    }
}

:deep([data-pc-section="menucontent"] a) {
    padding: 8px;
    margin-left: 1rem;
    padding-left: 3.5rem !important;
    &.active-link {
        background-color: #c7c9c7 !important;
        &:hover {
            background-color: #b3b5b3 !important;
        }
    }
}

:deep([data-pc-section="panel"]) {
    ul {
        margin: 2px 0 2px -2rem;
        padding-left: 0;
    }
}
</style>